/**
 * CreateIntentPicker — Bottom sheet that disambiguates the create intents.
 * Personal-only entries (Goal / Dream / Gift / Certificate). "Assign a task"
 * has been promoted to its own home-screen button.
 */
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Target, Sparkles, Gift, Award, ChevronRight } from "lucide-react";

export type CreateIntent = "goal" | "dream" | "gift" | "certificate";

interface CreateIntentPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (intent: CreateIntent) => void;
}

type Option = {
  intent: CreateIntent;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  example: string;
  gradient: string;
};

const OPTIONS: Option[] = [
  {
    intent: "goal",
    icon: Target,
    title: "A Goal",
    description: "Something I want to achieve — or save to my UAE Bucket List",
    example: "e.g. run a 5k, learn Arabic, visit Japan",
    gradient: "from-orange-500 to-rose-500",
  },
  {
    intent: "gift",
    icon: Gift,
    title: "List of Gifts I'd Prefer",
    description: "Something I'd love to receive",
    example: "Share with friends & family",
    gradient: "from-pink-500 to-rose-500",
  },
  {
    intent: "certificate",
    icon: Award,
    title: "A Certificate",
    description: "Celebrate something you've achieved",
    example: "e.g. completion, recognition",
    gradient: "from-amber-500 to-orange-500",
  },
];

function Row({ option, onPick, onClose }: { option: Option; onPick: (i: CreateIntent) => void; onClose: () => void }) {
  const { intent, icon: Icon, title, description, example, gradient } = option;
  return (
    <button
      type="button"
      onClick={() => {
        onPick(intent);
        onClose();
      }}
      className="group flex items-center gap-4 rounded-2xl border bg-card hover:bg-accent transition-all p-4 text-left shadow-sm hover:shadow-md active:scale-[0.99]"
      style={{ touchAction: "manipulation" }}
    >
      <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0 shadow-md`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-foreground">{title}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
        <p className="text-xs text-muted-foreground/80 mt-0.5">{example}</p>
      </div>
      <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
    </button>
  );
}

export function CreateIntentPicker({ open, onOpenChange, onPick }: CreateIntentPickerProps) {
  const close = () => onOpenChange(false);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-3xl max-h-[90vh] overflow-y-auto">
        <SheetHeader className="text-left">
          <SheetTitle className="text-2xl flex items-center gap-2">
            <span aria-hidden>🎯</span> Achieve it personal
          </SheetTitle>
          <SheetDescription>
            Pick what you'd like to set up — we'll guide you from there.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 pb-4 space-y-3" style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}>
          {OPTIONS.map((o) => <Row key={o.intent} option={o} onPick={onPick} onClose={close} />)}
        </div>
      </SheetContent>
    </Sheet>
  );
}
